"use client";

import Image from "next/image";
import { useMemo } from "react";
import {
  MdOutlineBed,
  MdOutlineBathtub,
  MdOutlineSquareFoot,
  MdFavorite,
  MdFavoriteBorder,
} from "react-icons/md";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import { toast } from "react-toastify";

import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { useCurrenciesContext } from "@/global-context/currencies-context";
import { useListingsCacheStore } from "@/global-cache/useListingsCacheStore";
import { m2ToSqft } from "@/lib/sqftToM2";
import { formatPrice } from "@/lib/price-formatter";
import { makeLocalizedHrefNoLocale } from "@/lib/localizedRoutes/routeHelpers";
import { resolveCardSavedError } from "@/lib/resolve-listing-card-saved-state";
import { getMedia } from "@/lib/media/shared";
import { useOptimalImageSize } from "@/hooks/useOptimalImageSize";
import { env } from "@/lib/env";
import { ApiError, Currencies, ListingCardData, TypeEnum } from "@/types/types";

import styles from "./home-listing-card.module.css";

const LISTINGSENDPOINTURL = env.NEXT_LISTINGS_API_URL as string;

export default function HomeListingCard({ data }: { data: ListingCardData }) {
  const { userData } = useUserAuthDataContext();
  const { currenciesRates } = useCurrenciesContext();
  const currencyKey = `EUR-${userData.currency}` as keyof Currencies;
  const rate: number = currenciesRates[currencyKey];
  const t = useTranslations("ListingCard");
  const locale = useLocale();
  const router = useRouter();
  const optimalImageSize = useOptimalImageSize();

  const isSaved = useListingsCacheStore(
    (state) => state.meta[data.listing_id]?.is_saved,
  );
  const updateSavedState = useListingsCacheStore(
    (state) => state.updateSavedState,
  );

  const listingUrl = makeLocalizedHrefNoLocale(locale, "listing", [
    `${data.listing_id}`,
  ]);

  const handleSmartClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    router.push(listingUrl);
  };

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (userData.idToken === null) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return;
    }

    const endpoint = isSaved
      ? "/removeFromSavedListings"
      : "/addToSavedListings";
    const attemptedState = !isSaved;

    updateSavedState(data.listing_id, attemptedState);

    try {
      const response = await fetch(`${LISTINGSENDPOINTURL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: userData.idToken,
        },
        body: JSON.stringify({ listing_id: data.listing_id }),
      });

      if (!response.ok) {
        const apiErr = (await response.json()) as ApiError;
        resolveCardSavedError({
          apiErr,
          listingId: data.listing_id,
          attemptedState,
          updateSavedState,
        });
        throw new Error(apiErr.message);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to update saved status."
          : errorMessage,
        { type: "error" },
      );
    }
  };

  const mainPhoto = useMemo(() => {
    const allPhotos = Object.entries(data.photos || {})
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([_, item]) => item);
    const photo = allPhotos[0];
    if (!photo) return null;

    const resolvedMedia = getMedia(photo, optimalImageSize);
    return {
      ...photo,
      resolvedMedia,
      displaySrc:
        resolvedMedia?.src || (photo as any).localUrl || "/broken_image.webp",
      displayWebp: resolvedMedia?.webp,
    };
  }, [data.photos, optimalImageSize]);

  const priceText = data.price_on_request
    ? t("findPrice")
    : `${formatPrice(rate * data.price)} ${userData.currency}${
        data?.purpose === "for-rent"
          ? data.is_price_per_year
            ? ` / ${t("year")}`
            : ` / ${t("month")}`
          : ""
      }`;

  const areaText =
    userData.unit_system === "Metric"
      ? data.type !== TypeEnum.LAND
        ? data.interior_space
        : data.lot_size
      : m2ToSqft(
          data.type !== TypeEnum.LAND ? data.interior_space : data.lot_size,
        );

  return (
    <article className={styles.card}>
      <a
        href={listingUrl}
        draggable={false}
        role="link"
        tabIndex={0}
        onClick={handleSmartClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleSmartClick(e);
        }}
        className={styles.linkWrapper}
      >
        {/* 1. The 16:10 Image Container */}
        <figure className={styles.imageContainer}>
          {mainPhoto ? (
            <picture style={{ display: "contents" }}>
              {mainPhoto.displayWebp && (
                <source srcSet={mainPhoto.displayWebp} type="image/webp" />
              )}
              <Image
                className={styles.image}
                src={mainPhoto.displaySrc}
                width={600}
                height={400}
                unoptimized={true}
                alt={data.title || t("propertyPhoto")}
                draggable={false}
                priority={true}
                placeholder="blur"
                blurDataURL="/placeholder_image.jpg"
              />
            </picture>
          ) : (
            <div className={styles.noImageFallback}>
              <Image
                src="/broken_image.webp"
                width={300}
                height={200}
                alt="No photos"
              />
            </div>
          )}

          <div className={styles.imageGradient} />

          <button
            className={styles.favoriteBtn}
            onClick={handleFavoriteClick}
            aria-label="Toggle Favorite"
          >
            {isSaved ? (
              <MdFavorite className={styles.heartActive} />
            ) : (
              <MdFavoriteBorder className={styles.heartInactive} />
            )}
          </button>
        </figure>

        {/* 2. Structured Content Block */}
        <div className={styles.contentWrapper}>
          <div className={styles.primaryInfo}>
            <h3 className={styles.price}>{priceText}</h3>
            <h4 className={styles.title} title={data.title}>
              {data.title}
            </h4>
            <p className={styles.locationText} title={data.address}>
              {data.address}
            </p>
          </div>

          {/* 3. The Data Bar Footer */}
          <div className={styles.specsRow}>
            {data.number_of_bedrooms && (
              <div
                className={styles.specItem}
                title={`${data.number_of_bedrooms} Bedrooms`}
              >
                <MdOutlineBed className={styles.specIcon} />
                <span>{data.number_of_bedrooms}</span>
              </div>
            )}

            {data.number_of_bedrooms && data.number_of_bathrooms && (
              <span className={styles.specDivider} />
            )}

            {data.number_of_bathrooms && (
              <div
                className={styles.specItem}
                title={`${data.number_of_bathrooms} Bathrooms`}
              >
                <MdOutlineBathtub className={styles.specIcon} />
                <span>{data.number_of_bathrooms}</span>
              </div>
            )}

            {(data.number_of_bedrooms || data.number_of_bathrooms) &&
              areaText && <span className={styles.specDivider} />}

            <div className={styles.specItem} title={`${areaText} Area`}>
              <MdOutlineSquareFoot className={styles.specIcon} />
              <span>
                {areaText} {userData.unit_system === "Metric" ? "sqm" : "sqft"}
              </span>
            </div>
          </div>
        </div>
      </a>
    </article>
  );
}
